import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface SilenceState {
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationSilenceService {
  private readonly STORAGE_KEY = 'NotificationSilenceState';
  private deactivateTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly silenced$ = new BehaviorSubject<boolean>(false);
  public readonly silenced = this.silenced$.asObservable();

  constructor() {
    this.restoreFromStorage();
  }

  public get isSilenced(): boolean {
    return this.silenced$.getValue();
  }

  public activate(durationMs: number): void {
    const expiresAt = new Date(Date.now() + durationMs).toISOString();
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ expiresAt }));
    this.silenced$.next(true);
    this.scheduleDeactivate(durationMs);
  }

  public deactivate(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.silenced$.next(false);
    if (this.deactivateTimer !== null) {
      clearTimeout(this.deactivateTimer);
      this.deactivateTimer = null;
    }
  }

  private restoreFromStorage(): void {
    const raw = localStorage.getItem(this.STORAGE_KEY);
    if (!raw) return;
    try {
      const state: SilenceState = JSON.parse(raw);
      const remaining = new Date(state.expiresAt).getTime() - Date.now();
      if (remaining > 0) {
        this.silenced$.next(true);
        this.scheduleDeactivate(remaining);
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private scheduleDeactivate(ms: number): void {
    if (this.deactivateTimer !== null) {
      clearTimeout(this.deactivateTimer);
    }
    this.deactivateTimer = setTimeout(() => this.deactivate(), ms);
  }
}
